/**
 * TanStack Query key factories for every API domain — the single source of truth for
 * cache keys. Hooks in `hooks/*.ts` must build keys only through `qk`.
 *
 * Keys are hierarchical so a prefix invalidates everything below it:
 *
 *   ['projects', pid]                       everything of one project
 *     ['projects', pid, 'detail']            Project (embeds classes + members)
 *     ['projects', pid, 'classes']           ['projects', pid, 'members']
 *     ['projects', pid, 'stats']             ['projects', pid, 'next-task', {…}]
 *     ['projects', pid, 'images', {…}]       image pages (list params as last element)
 *     ['projects', pid, 'exports']           ['projects', pid, 'models']
 *   ['images', iid]                         everything of one image
 *     ['images', iid, 'detail']              ['images', iid, 'annotations']
 *     ['images', iid, 'spectrum', {…}]       ['images', iid, 'sam', 'prepare']
 *   ['annotations', aid]                    detail / reviews / history
 *   ['jobs']                                every job query (lists + details)
 *
 * Rules: invalidate the narrowest prefix that covers what a mutation changed; class and
 * member mutations also invalidate `projects.detail(pid)` (the Project embeds both) and
 * `projects.list()` (counts). Put variable parameters in a trailing object so partial
 * matching works (`qk.images.lists(pid)` matches every page of that project).
 */
import type { ImageListParams, JobListParams, NextTaskParams } from './types';

export const qk = {
  auth: {
    me: () => ['auth', 'me'] as const,
    tokens: () => ['auth', 'tokens'] as const,
    invite: (token: string) => ['auth', 'invite', token] as const,
  },
  users: {
    all: () => ['users'] as const,
    list: () => ['users', 'list'] as const,
    lookup: (q: string) => ['users', 'lookup', q] as const,
  },
  projects: {
    all: () => ['projects'] as const,
    list: () => ['projects', 'list'] as const,
    scope: (pid: number) => ['projects', pid] as const,
    detail: (pid: number) => ['projects', pid, 'detail'] as const,
    stats: (pid: number) => ['projects', pid, 'stats'] as const,
    nextTask: (pid: number, params: NextTaskParams) => ['projects', pid, 'next-task', params] as const,
  },
  classes: {
    list: (pid: number) => ['projects', pid, 'classes'] as const,
  },
  members: {
    list: (pid: number) => ['projects', pid, 'members'] as const,
  },
  images: {
    /** Every image page of a project (prefix of {@link qk.images.list}). */
    lists: (pid: number) => ['projects', pid, 'images'] as const,
    list: (pid: number, params: ImageListParams) => ['projects', pid, 'images', params] as const,
    /** Infinite (page-by-page) image list; `params` without offset. */
    infinite: (pid: number, params: ImageListParams) =>
      ['projects', pid, 'images', params, 'infinite'] as const,
    scope: (iid: number) => ['images', iid] as const,
    detail: (iid: number) => ['images', iid, 'detail'] as const,
    spectrum: (iid: number, params: { x: number; y: number; r?: number }) =>
      ['images', iid, 'spectrum', params] as const,
  },
  uploads: {
    blocks: (uploadId: string, fileIdx: number) => ['uploads', uploadId, 'files', fileIdx, 'blocks'] as const,
  },
  annotations: {
    list: (iid: number) => ['images', iid, 'annotations'] as const,
    scope: (aid: number) => ['annotations', aid] as const,
    detail: (aid: number) => ['annotations', aid, 'detail'] as const,
    reviews: (aid: number) => ['annotations', aid, 'reviews'] as const,
    history: (aid: number) => ['annotations', aid, 'history'] as const,
    /** Which annotation the annotator opens for an image (get-or-create, `?annotation=`). */
    session: (iid: number, params: { annotation: number | null; user: number; review?: boolean }) =>
      ['images', iid, 'annotator-session', params] as const,
  },
  tools: {
    mlStatus: () => ['ml', 'status'] as const,
    samPrepare: (iid: number) => ['images', iid, 'sam', 'prepare'] as const,
  },
  autoannotate: {
    backends: () => ['autoannotate', 'backends'] as const,
    models: (pid: number) => ['projects', pid, 'models'] as const,
    /** JSON Schema of the pixel-classifier training params (from the OpenAPI document). */
  },
  jobs: {
    all: () => ['jobs'] as const,
    list: (params: JobListParams) => ['jobs', 'list', params] as const,
    detail: (jid: number) => ['jobs', 'detail', jid] as const,
  },
  exports: {
    formats: () => ['export-formats'] as const,
    list: (pid: number) => ['projects', pid, 'exports'] as const,
  },
  system: {
    info: () => ['system', 'info'] as const,
    health: () => ['system', 'health'] as const,
  },
} as const;
